// ============================================================
// TILT — AENERGY  (src/Aenergy.js)
// ------------------------------------------------------------
// THE NUMERIC TRUTH. TILT's rules.js.
//
// Authority chain (from TILT_CONFORMANCE.md):
//   IE_POWER_SYSTEM_BLUEPRINT.md  →  TILT_CONFORMANCE.md  →
//   THIS FILE  →  Enforcer.js
//
// When any other file disagrees with a number in here, THIS FILE
// WINS and the other file is a bug. No component, table, or screen
// may hardcode any constant that appears below. The HUD reads this.
// The engine reads this. The Enforcer reads this. The NPC reads
// this. One edit, everything moves. (The PackArt.js law.)
//
// ── THE ONE IDEA ─────────────────────────────────────────────
//   THE BALL IS THE TURN.
// Khao refills on launch. Lyf restores on drain. OPT = once per
// ball. The Growth Law checks the ball you just finished. Every
// timing word in this file means "ball" where Terror means "turn".
//
// ── WHAT LIVES HERE vs Powers.js vs Enforcer.js ──────────────
//   Aenergy.js  — numbers, ladders, derivations. NO effects.
//   Powers.js   — the registry. WHAT each power does.
//   Enforcer.js — the gate. WHETHER an action is legal.
// A number in Powers.js that isn't a per-power stat (cost, level,
// duration) is in the wrong file.
// ============================================================

// ══════════════════════════════════════════════════════════════
// 1 · ANIMA — life. The fail state. The wager.
// ------------------------------------------------------------
// Identical constants to Terror and Sol Survivor (30/50). This is
// deliberate — the blueprint audits Sol as "✅ Identical constants.
// Correct." and TILT should pass the same line of the same audit.
//
// ⚠ BALLS ARE A VIEW. ballsFor() below is the ONLY place a ball
//   count is ever derived, and it is NEVER stored. Storing it is
//   the getTierForRenown() violation wearing a costume: two
//   sources of truth that will drift, and the drift will be a
//   player who fired a U'hlti and didn't lose a ball.
// ══════════════════════════════════════════════════════════════
export const ANIMA_START = 30;
export const ANIMA_MAX   = 50;
export const DRAIN_DIM   = 10;   // a drain dims 10 Anima ( = one "ball")

/** The ball rack, derived. 30 Anima = 3 balls. 29 = 3 (you're on
 *  your last third). 0 = over. Math.ceil, so partial Anima still
 *  shows a ball — you always SEE what you have left to lose. */
export const ballsFor = (anima) =>
  Math.max(0, Math.ceil(Math.max(0, anima) / DRAIN_DIM));

// The sanctioned voluntary spends. The Anima Doctrine: your life
// bar is a currency you are ALLOWED to spend, and the smartest
// plays are the ones that spend it. Anything not listed here is
// not allowed to touch Anima as a cost. (U'hlti costs are per-power
// and live in Powers.js — this table is the ENGINE-level spends.)
export const ANIMA_SPENDS = {
  relight:      10,   // The Relight (Graveshift). See §5.
  forceMode:     5,   // light a mode the table didn't give you
  deviantAscend: 4,   // skip the chain. Cap the zone forever.
};

// ══════════════════════════════════════════════════════════════
// 2 · KHAO — fuel. And AENERGY beneath it.
// ------------------------------------------------------------
// Two meters, one economy:
//
//   AENERGY — earned by PLAY. Every chargeTable number in every
//             table file, unchanged, byte for byte. Aggressive,
//             high-contact play funds it; cradling starves it.
//             (The old Powers.js law, kept whole.)
//   KHAO    — the integer meter powers actually cost. AENERGY
//             condenses into KHAO at AENERGY_PER_KHAO : 1, capped
//             at Max Khao. Refills to Max on every LAUNCH.
//
// ★ THE GROWTH LAW (R12): end a ball having spent ZERO Khao →
//   Max Khao +1. Play refills what you spend within the ball;
//   abstention raises the ceiling across the game. The two laws
//   argue with each other. That argument is the game.
//
// ⚠ AENERGY_PER_KHAO is the R1 tuning knob (TILT_CONFORMANCE §9).
//   If the sim shows the power economy starving at Max 3, THIS
//   ratio moves — the cap does not. The cap is scripture; the
//   ratio is tuning.
// ══════════════════════════════════════════════════════════════
export const KHAO_START_MAX = 3;
export const KHAO_NAT_MAX   = 10;   // natural ceiling (Growth Law stops here)
export const KHAO_ABS_MAX   = 15;   // absolute ceiling (Tomes only — Khao Garden)
export const AENERGY_PER_KHAO = 10;

// Aenergy overflow above a full Khao meter is BANKED up to one
// Khao's worth, then spills. Without a bank, a full meter makes
// every bumper hit worthless and punishes the exact play style
// the meter exists to reward. With an infinite bank, the Growth
// Law dies (you'd bank a game's worth and never feel the cap).
export const AENERGY_BANK_MAX = AENERGY_PER_KHAO;

// ══════════════════════════════════════════════════════════════
// 3 · LYT & LYF — the edge and the skin.
// ------------------------------------------------------------
// LYT is COMBO HEAT, not ball speed. Speed already pays (the
// _award speed bonus); making Lyt speed-derived would double-pay
// the same input. Combo is the skill signal speed isn't: you
// cannot combo by cradling.
//
// ★ THE LYF CYCLE (R27/28): a device's guard RESTORES IN FULL on
//   every drain. Chip damage is worthless. The way through a wall
//   is not more shots — it is HOTTER shots, landed this ball.
//
// ★ THE SOUL LINE (do not blur this — it's how a careless port
//   would destroy Foundry): SOUL persists across a drain; LYF
//   resets across a drain. Progress attaches to the ZONE;
//   durability attaches to the DEVICE. Foundry's tickets and the
//   Forgemaster's heat are Soul. A guard is Lyf.
//
// R29: a device authored with lyf: 0 is PERMANENTLY FRACTURED —
// always open. That is the table author's word for "no guard".
// ══════════════════════════════════════════════════════════════
export const LYT_BASE      = 1;
export const LYT_PER_COMBO = 3;    // +1 Lyt per 3 combo
export const LYT_CAP       = 5;
export const LYT_OVERCLOCK = 1;    // Overclock adds +1 while live

/** The ONLY place Lyt is ever computed. */
export const lytFor = (combo = 0, overclocked = false) =>
  Math.min(LYT_CAP, LYT_BASE + Math.floor(Math.max(0, combo) / LYT_PER_COMBO))
  + (overclocked ? LYT_OVERCLOCK : 0);

// ══════════════════════════════════════════════════════════════
// 4 · THE LATTICE — Class × Ilk × Level.
// ------------------------------------------------------------
// Four zones per table, DECLARED BY THE TABLE (a `zones:` block —
// see assertLattice in TableKit). The four Ancestries are fixed;
// which geography wears them is the table's art.
// 4 zones × 3 tiers = 12 Lines. A proportional shrink of Terror's
// 36; the blueprint blesses it.
// ══════════════════════════════════════════════════════════════
export const ZONE_IDS = ["space", "phantom", "time", "dragon"];

export const ANCESTRIES = {
  space:   { class: "humanoid", ilk: "space",   escling: "Space Escling"   },
  phantom: { class: "beast",    ilk: "phantom", escling: "Phantom Escling" },
  time:    { class: "machine",  ilk: "time",    escling: "Time Escling"    },
  dragon:  { class: "dragon",   ilk: "dragon",  escling: "Dragon Escling"  },
};

// Levels. The Escling is not a rung you climb to — it is the
// pre-placed potential every zone STARTS as. The entire game is
// the act of realizing it.
export const LEVEL = { ESCLING: 0, BEB: 1, CHAMPION: 2, LORD: 3 };
export const LEVEL_NAMES = ["Escling", "Beb", "Champion", "Lord"];

/** Ascend chain length: how many chain shots to reach the NEXT
 *  level. Beb is 1 shot away from Escling; Lord is 3 from
 *  Champion. chainNeed(currentLevel) → shots required. */
export const chainNeed = (level) => Math.min(3, Math.max(1, level + 1));

// ★ ONCE PER BALL — the load-bearing constraint. One Ascend per
// zone per ball means no Lord before ball three, ever. The game
// has SHAPE because of this number. It is 1. It stays 1.
export const ASCENDS_PER_BALL = 1;

// Resurrection (R44–48): a zone knocked down by a table U'hlti may
// be raised once per run, IN LEVEL ORDER, Souls kept. Cost is Khao
// OR Anima, payer's choice (the Anima Doctrine again).
export const RESURRECT_COST = { 1: 2, 2: 4, 3: 4 };   // by target level

// ══════════════════════════════════════════════════════════════
// 5 · THE DARK & THE RELIGHT — the Necropolis, and the killed
//     bookkeeping death.
// ------------------------------------------------------------
// THE DARK: every shot that has gone out. A resource pool, not a
// trash can. THE RELIGHT: Khao at 0 and nothing lit → pay exactly
// 10 Anima; everything in the Dark comes back LIT and Khao refills
// to Max. YOU CANNOT LOSE TO A DEAD TABLE. Only the fail state is
// the fail state.
// ══════════════════════════════════════════════════════════════
export const RELIGHT_COST = ANIMA_SPENDS.relight;   // one source of truth

// ══════════════════════════════════════════════════════════════
// 6 · CADENCE — copied verbatim. The Empire's clock.
// ------------------------------------------------------------
//   OPT — once per BALL          OP2 — once per 2 balls
//   OP3 — once per 3 balls       ATV — Active or Total Void
//   ACB — Adversary Chooses Between    SSG — Shadow Gambling flag
//
// THE FARE LAW: a cadence-tagged power's Khao cost is paid the
// FIRST time it fires, never again. You paid to light the fire;
// the fire keeps burning.
// THE RUIN LAW (R39): no cadence tag → ruined after one use.
// ══════════════════════════════════════════════════════════════
export const CADENCE = { OPT: "OPT", OP2: "OP2", OP3: "OP3", ATV: "ATV" };
export const PERSISTENT_TAGS = ["OPT", "OP2", "OP3", "ATV"];

/** Balls between fires for a cadence tag. ATV is continuous. */
export const cadenceBalls = (tag) =>
  tag === "OPT" ? 1 : tag === "OP2" ? 2 : tag === "OP3" ? 3 : 0;

// ══════════════════════════════════════════════════════════════
// 7 · THE RESPONSE WINDOW — the ball freezes.
// ------------------------------------------------------------
// The table DECLARES. Every live ball freezes for WINDOW_SECONDS
// (the physics already ships this — ball.frozen is deterministic
// and tested). You answer with a held Tome, or you don't.
//
// ⚠ THE COUNTER HIERARCHY (the rule people miss): an L1/L2
//   counter CANNOT pre-empt an L3+ declaration. It arrives LATE —
//   the mode fires, THEN dies. Only L3+ counters get pre-emptive
//   treatment. Below L3, rank means nothing: an L1 stops an L2
//   just fine. canPreempt() below is the ONLY place this rule is
//   ever evaluated.
// ══════════════════════════════════════════════════════════════
export const WINDOW_SECONDS = 5.0;

export const canPreempt = (counterLevel, declarationLevel) =>
  declarationLevel < 3 || counterLevel >= 3;

// ══════════════════════════════════════════════════════════════
// 8 · THE SEVEN RUNGS — the IE-wide ladder. These exact words.
// ------------------------------------------------------------
// The player's rank, DERIVED from lifetime Renown, NEVER stored.
// Same thresholds as the Endless Depths Elo bands.
// ══════════════════════════════════════════════════════════════
export const RANKS = [
  { id: "escling",    name: "Escling",    min: 0    },
  { id: "beb",        name: "Beb",        min: 400  },
  { id: "champion",   name: "Champion",   min: 800  },
  { id: "lord",       name: "Lord",       min: 1200 },
  { id: "escendant",  name: "Escendant",  min: 1700 },
  { id: "aenarchist", name: "Aenarchist", min: 2200 },
  { id: "solrend",    name: "Solrend",    min: 2800 },
];

/** getTierForRenown(), TILT edition. The single source of truth. */
export const rankForRenown = (renown = 0) => {
  let r = RANKS[0];
  for (const rank of RANKS) if (renown >= rank.min) r = rank;
  return r;
};

// ══════════════════════════════════════════════════════════════
// 9 · PROVENANCE — the SourceSigil.
// ------------------------------------------------------------
// Every grant remembers where it came from. Earned flares;
// purchased stays quiet. A moral position expressed as a shader.
// ══════════════════════════════════════════════════════════════
export const RADIANCE = { FLAT: 0, GLIMMER: 1, SHIMMER: 2, RADIANT: 3 };

/** Stamp a grant. source: "shop" | "zone:space" | "uhlti:dragon" |
 *  "trial:t02" ... — a string the Grimoire screen can render. */
export const sigil = (source, radiance = RADIANCE.FLAT) => ({
  source: String(source || "unknown"),
  radiance: Math.max(0, Math.min(3, radiance | 0)),
});

// ══════════════════════════════════════════════════════════════
// 10 · SUPREME & TITLES
// ══════════════════════════════════════════════════════════════
export const TITLES = ["A'eternuz", "D'aeternuz", "V'igiluz", "Jester"];

/** The Supreme Condition (R69–73), evaluated in ONE place.
 *  A zone is Supreme iff: Natural Lord + 3 Impera + U'hlti + Affinity. */
export const isSupreme = (z) =>
  !!z && z.level === LEVEL.LORD && !z.deviant &&
  (z.impera?.length || 0) >= 3 && !!z.uhlti && !!z.affinity;

// ══════════════════════════════════════════════════════════════
// 11 · THE UNIVERSAL POWER OBJECT — the schema Powers.js fills.
// ------------------------------------------------------------
// Documented here so the shape has one home. validatePower() is
// what selftest.js runs against the whole registry.
//
// {
//   id, name, glyph, blurb,          // identity (id NEVER renamed)
//   kind,                            // "impera"|"tome"|"uhlti"|"affinity"|"prophecy"
//   zone,                            // Ancestry key, or null (Tomes are free agents)
//   level,                           // 1|2|3 — gates counters (canPreempt)
//   khaoCost, animaCost,             // both poles, always (0 is a value)
//   cadence,                         // "OPT"|"OP2"|"OP3"|"ATV"|null
//   ssg,                             // Shadow Gambling participant?
//   target,                          // "self"|"point"|"enemy"|"window"|null
//   sabotage,                        // may be cast at an opponent's table
//   duration,                        // seconds (0 = instant)
//   tint,                            // theme key
//   unlock,                          // { level } for Impera, etc.
//   effect,                          // switch key for engine-resolved powers
//   apply(ctx) / onEnemy(ctx)        // physics verbs (Impera keep these)
// }
// ══════════════════════════════════════════════════════════════
export const POWER_KINDS = ["impera", "tome", "uhlti", "affinity", "prophecy"];

export const validatePower = (p) => {
  const bad = [];
  if (!p?.id)                          bad.push("missing id");
  if (!POWER_KINDS.includes(p?.kind))  bad.push(`bad kind "${p?.kind}"`);
  if (p?.zone && !ZONE_IDS.includes(p.zone)) bad.push(`bad zone "${p.zone}"`);
  if (![1, 2, 3].includes(p?.level))   bad.push(`bad level ${p?.level}`);
  if (typeof p?.khaoCost  !== "number" || p.khaoCost  < 0) bad.push("bad khaoCost");
  if (typeof p?.animaCost !== "number" || p.animaCost < 0) bad.push("bad animaCost");
  if (p?.cadence && !PERSISTENT_TAGS.includes(p.cadence))  bad.push(`bad cadence "${p.cadence}"`);
  if (p?.kind === "uhlti" && !(p.khaoCost > 0 && p.animaCost > 0))
    bad.push("a U'hlti must charge BOTH Khao and Anima — that's what makes it a U'hlti");
  if (p?.kind === "affinity" && p.khaoCost !== 0)
    bad.push("an Affinity costs 0 Khao — it costs nothing but everything before it");
  return { valid: bad.length === 0, violations: bad };
};

// ══════════════════════════════════════════════════════════════
// 12 · THE GRIMOIRE — loadout constants.
// ------------------------------------------------------------
// The player brings TOME_SLOTS Tomes to a table. Imperas are not
// slotted — they are EARNED in-run and Soul-bound to their zone.
// Nudge is always free and never occupies a slot. (Unchanged law.)
// ══════════════════════════════════════════════════════════════
export const TOME_SLOTS = 4;

export default {
  ANIMA_START, ANIMA_MAX, DRAIN_DIM, ballsFor, ANIMA_SPENDS,
  KHAO_START_MAX, KHAO_NAT_MAX, KHAO_ABS_MAX, AENERGY_PER_KHAO, AENERGY_BANK_MAX,
  LYT_BASE, LYT_PER_COMBO, LYT_CAP, LYT_OVERCLOCK, lytFor,
  ZONE_IDS, ANCESTRIES, LEVEL, LEVEL_NAMES, chainNeed, ASCENDS_PER_BALL,
  RESURRECT_COST, RELIGHT_COST,
  CADENCE, PERSISTENT_TAGS, cadenceBalls,
  WINDOW_SECONDS, canPreempt,
  RANKS, rankForRenown,
  RADIANCE, sigil,
  TITLES, isSupreme,
  POWER_KINDS, validatePower, TOME_SLOTS,
};
